import DataTable from '../../reusable/DataTable/DataTable';
import { PageSize, pageWindow } from '../../reusable/DataTable/Pagination';
import { signedUsd } from '../../utilities/format';
import StatusChip from '../../reusable/StatusChip/StatusChip';
import { t } from '../../i18n/i18n';
import './LeaderboardTable.scss';

const RANK_TIER = { 1: 'gold', 2: 'silver', 3: 'bronze' };

// Maps to StatusChip's existing tones only — no new signal colours.
const STATUS_TONE = { Trading: 'success', Eliminated: 'danger', Completed: 'info' };

/** `return_percentage` arrives at full float precision (334.63953999999995). */
const returnPct = (n) => {
  const v = Math.round((Number(n) || 0) * 100) / 100;
  return `${v < 0 ? '' : '+'}${v}%`;
};

const COLUMNS = [
  {
    key: 'rank',
    header: 'Rank',
    width: 0.7,
    searchable: false,
    render: (r) => {
      const tier = RANK_TIER[r.rank];
      return <span className={`lb-rank${tier ? ` lb-rank--${tier}` : ''}`}>{r.rank}</span>;
    },
  },
  {
    key: 'name',
    header: 'Trader',
    width: 2.2,
    render: (r) => (
      <span className="lb-trader">
        {r.name}
        {r.is_you && <span className="lb-trader__you">{t('(You)')}</span>}
      </span>
    ),
  },
  { key: 'country', header: 'Country', width: 1.6 },
  {
    key: 'total_returns',
    header: 'Profit',
    width: 1.4,
    searchable: false,
    // Fixed to 2dp: live returns arrive fractional (334639.54 beside
    // 202154.7), and the default would ragged the column.
    value: (r) => signedUsd(r.total_returns, { decimals: 2 }),
    tone: (r) => (r.total_returns >= 0 ? 'up' : 'down'),
  },
  {
    key: 'return_percentage',
    header: 'Return',
    width: 1.2,
    searchable: false,
    value: (r) => returnPct(r.return_percentage),
    // The percentage and the amount are the same move, so they carry the same
    // colour — and the amount is the one that can't round to +0.00%.
    tone: (r) => (r.total_returns >= 0 ? 'up' : 'down'),
  },
  {
    key: 'win_rate',
    header: 'Win rate',
    width: 1.1,
    searchable: false,
    value: (r) => `${r.win_rate}%`,
  },
  {
    key: 'trades',
    header: 'Trades',
    width: 0.8,
    searchable: false,
    // A board row counts them as `trades`; the payload's `user` block — which
    // is what the pinned row is — calls the same number `total_trades`.
    value: (r) => r.trades ?? r.total_trades,
  },
  {
    key: 'status',
    header: 'Status',
    width: 1.0,
    searchable: false,
    // A disqualified trader is named as one — the payload still says
    // "Trading" for them. The `user` block carries no status at all, so the
    // pinned row leaves the cell empty rather than drawing a blank chip.
    render: (r) => {
      const status = r.is_disqualified ? 'Eliminated' : r.status;
      return status ? <StatusChip tone={STATUS_TONE[status]}>{t(status)}</StatusChip> : null;
    },
  },
];

/**
 * Competition leaderboard, built on the shared `DataTable` (toolbar, search,
 * tone-coloured cells, row selection) rather than a bespoke grid — reused
 * as-is by the Overview section (1:14844, with search/filters/pagination and
 * a pinned current-user row) and the Competition Detail page (simplified: no
 * count, a plain subtitle instead of search/filters, no pagination).
 *
 * `rows` and `currentUser` are `/v2/competition/leaderboard/` rows as they
 * arrive: the columns are keyed on the payload's own field names, so neither
 * page maps anything on the way in.
 *
 * `meta` is `leaderboard.meta` as the payload sends it, and passing it is what
 * gives the table a footer at all. The endpoint pages the board — 6,726
 * traders, as many pages as `page_size` makes of them — so `rows` is one page
 * the server has already searched, filtered and cut, and nothing is sliced
 * here. `page` / `onPageChange` own the page number, and `onSearch` hands the
 * search box's term back to whoever builds the request. The Detail page's
 * read-only board passes none of them and gets no footer, as before.
 *
 * `loading` draws `loadingRows` skeleton rows under the real header instead
 * of the empty message — for the board's own request, and for any call the
 * caller is still waiting on before that one can even be made.
 *
 * `currentUser` is pinned at the bottom, distinctly highlighted (DataTable's
 * existing `selectedId` row-highlight), whenever their rank falls outside
 * `rows` — omit it where there's no logged-in-user row to pin (e.g. the
 * Detail page's read-only leaderboard).
 */
export default function LeaderboardTable({
  rows,
  currentUser,
  meta,
  page,
  onPageChange,
  /* The board's own footer, not DataTable's pager, so the rows-per-page
     control is rendered here from the same component the pager uses. */
  pageSize,
  pageSizeOptions,
  onPageSizeChange,
  updatedLive = false,
  title = 'Leaderboard',
  count,
  subtitle,
  searchPlaceholder,
  onFilters,
  filter,
  onSearch,
  loading = false,
  loadingRows,
}) {
  /* The size the rows in hand were cut at — which is the response's, not the
     selector's: through a size change the selector leads by one request, and
     `from`/`to` describe the page actually on screen. */
  const metaSize = meta?.page_size ?? rows.length;
  const pageCount = meta?.total_pages ?? 1;
  // The caller's page leads `meta.page`, so pressing 4 marks 4 straight away
  // rather than a request later.
  const current = page ?? meta?.page ?? 1;
  const from = rows.length === 0 ? 0 : (current - 1) * metaSize + 1;
  const to = rows.length === 0 ? 0 : from + rows.length - 1;

  /* The selector first, so switching to 50 draws 50 skeleton rows straight
     away rather than ten until the response says so. */
  const skeletonRows = loadingRows ?? pageSize ?? meta?.page_size ?? 10;

  // The trader's own row is pinned to every page, not just the one their rank
  // falls on — the card's job is to answer "where am I" at a glance.
  const showPinned = currentUser && !rows.some((r) => r.rank === currentUser.rank);
  // `user` is the trader by definition — it does not have to say `is_you` the
  // way a row inside `leaderboard` does for the Trader column to badge it.
  const tableRows = showPinned ? [...rows, { ...currentUser, is_you: true }] : rows;

  const step = (next) => () => onPageChange?.(Math.min(Math.max(next, 1), pageCount));
  const showSizes = Boolean(pageSizeOptions?.length && onPageSizeChange);

  return (
    <DataTable
      className="lb-table"
      icon="nav-competition"
      title={t(title)}
      count={count}
      columns={COLUMNS}
      rows={tableRows}
      getRowId={(r) => r.rank}
      minWidth={900}
      loading={loading}
      // `total` puts DataTable in its own server mode: it draws `rows` as the
      // page and skips the search, sort and slice it would otherwise apply to
      // a page the endpoint has already searched, filtered and cut.
      total={meta ? tableRows.length : undefined}
      pageSize={loading ? skeletonRows : Math.max(tableRows.length, 1)}
      searchPlaceholder={searchPlaceholder}
      onSearch={onSearch}
      onFilters={onFilters}
      filter={filter}
      selectedId={showPinned ? currentUser.rank : undefined}
      pagination={false}
      actions={subtitle && <span className="lb-subtitle">{subtitle}</span>}
      footer={
        meta && (
          <footer className="lb-foot">
            <div className="lb-foot__left">
              <span className="lb-foot__summary">
                {loading
                  ? '—'
                  : `${t('{{from}}–{{to}} of {{total}}', {
                      from,
                      to,
                      total: meta.total ?? rows.length,
                    })}${updatedLive ? ` · ${t('updated live')}` : ''}`}
              </span>

              {showSizes && (
                <PageSize
                  value={pageSize ?? metaSize}
                  options={pageSizeOptions}
                  onChange={onPageSizeChange}
                />
              )}
            </div>

            {pageCount > 1 && (
              <div className="lb-pager">
                {/* Windowed, with a step at each end: the board runs to
                    hundreds of pages, and a button each would be the row. */}
                <button
                  type="button"
                  className="lb-pager__page"
                  onClick={step(current - 1)}
                  disabled={current <= 1}
                  aria-label={t('Previous page')}
                >
                  &lsaquo;
                </button>

                {pageWindow(current, pageCount).map((n) => (
                  <button
                    key={n}
                    type="button"
                    className={`lb-pager__page${n === current ? ' is-active' : ''}`}
                    onClick={step(n)}
                    aria-current={n === current ? 'page' : undefined}
                  >
                    {n}
                  </button>
                ))}

                <button
                  type="button"
                  className="lb-pager__page"
                  onClick={step(current + 1)}
                  disabled={current >= pageCount}
                  aria-label={t('Next page')}
                >
                  &rsaquo;
                </button>
              </div>
            )}
          </footer>
        )
      }
    />
  );
}
